import React, { ReactNode, memo, useEffect, useState } from 'react';

import { useLocation } from 'react-use';

import { Collapse } from '@material-ui/core';
import { Lock } from 'icons';
import { useIntl } from 'localization';
import { twMerge } from 'tailwind-merge';

import Badge from '../../../atoms/Badge';
import { DropdownExpandMoreIcon } from '../../Dropdown/DropdownExpandMoreIcon';
import { DropdownItem } from '../../Dropdown/DropdownItem';

type TabData = {
  value: string;
  label: ReactNode;
  locked?: boolean;
  isNew?: boolean;
  icon?: ReactNode;
};

export type AdminSettingsDisclosureProps = {
  className?: string;
  sectionName: ReactNode;
  openTab: string;
  onChangeTab(newTab: string): void;
  data?: TabData[];
  exact?: boolean;
  icon?: ReactNode;
  tabIndex: number;
  link?: string;
};

export const AdminSettingsDisclosure = ({
  className,
  sectionName,
  data,
  openTab,
  onChangeTab,
  exact = true,
  icon,
  tabIndex,
  link,
}: AdminSettingsDisclosureProps) => {
  const { formatMessage } = useIntl();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(
    tabIsOpened(tabIndex) || data?.some((x) => location?.pathname?.includes(x.value)),
  );

  const isSelected = data?.some((x) => location?.pathname?.includes(x.value));

  useEffect(() => {
    if (!isOpen && data?.some((x) => location?.pathname?.includes(x.value))) {
      setIsOpen(true);
    }
  }, [location?.pathname]);

  return (
    <div className={twMerge('text-sm', className)}>
      <DropdownItem
        onClick={() => {
          if (link) {
            onChangeTab(link);
            return;
          }
          setIsOpen((prev) => !prev);
          saveTabStateToLocalStorage(tabIndex, !isOpen);
        }}
        className={twMerge('gap-[6px] capitalize mb-0', isOpen && 'font-semibold')}
        icon={
          icon ? (
            <span className={!isSelected ? 'text-v2blueGray-550' : ''}>{icon}</span>
          ) : (
            <DropdownExpandMoreIcon open={isOpen} />
          )
        }
        selected={isSelected}
      >
        {sectionName}
      </DropdownItem>

      <Collapse in={isOpen}>
        <div className="pl-6 flex flex-col">
          {data?.map((entry: TabData, index) => {
            const selected = exact ? openTab === entry.value : openTab.startsWith(entry.value);

            return (
              <DropdownItem
                key={`${index}${entry.value}`}
                wrapperClassName="relative"
                disabled={entry.locked}
                icon={entry.locked ? <Lock className="w-5 h-5 text-v2blueGray-500" /> : entry.icon}
                className={twMerge(
                  'select-none',
                  entry.locked && 'text-v2blueGray-550 justify-between',
                  !entry.locked && !selected && 'text-v2blueGray-700',
                )}
                iconPosition={entry.icon ? 'left' : 'right'}
                selected={selected}
                ref={
                  selected
                    ? (el) => {
                        if (el) {
                          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                        }
                      }
                    : undefined
                }
                onClick={() => {
                  onChangeTab(entry.value);
                }}
              >
                {entry.label}
                {entry.isNew && (
                  <div className="absolute top-1/2 -translate-y-1/2 right-2">
                    <Badge text={formatMessage({ id: 'conversationsHub.new' })} theme="purple" />
                  </div>
                )}
              </DropdownItem>
            );
          })}
        </div>
      </Collapse>
    </div>
  );
};

const tabIsOpened = (tabIndex: number) => {
  const allTabsData = localStorage.getItem('adminSettingsTabIndex');
  if (allTabsData) {
    const parsedData = JSON.parse(allTabsData);
    return !!parsedData[tabIndex];
  }
  return false;
};

const saveTabStateToLocalStorage = (tabIndex: number, isOpen: boolean) => {
  const allTabsData = localStorage.getItem('adminSettingsTabIndex');
  const parsedData = allTabsData ? JSON.parse(allTabsData) : {};
  parsedData[tabIndex] = isOpen;
  localStorage.setItem('adminSettingsTabIndex', JSON.stringify(parsedData));
};

export default memo(AdminSettingsDisclosure);
